/*
 * Project: Goalscape
 * File:    workspaceMemberUtils.js
 * Path:    src/utils/workspaceMemberUtils.js
 * Description: Utility functions for managing workspace members and their roles.
 * Author:  Akmal Djumakhodjaev
 * Created: [Creation Date - YYYY-MM-DD]
 * Modified: 2025-03-15
 * AI Hint: Implement role-based access control (RBAC) to restrict access to certain features based on member roles.
 */
import { doc, updateDoc, getDocs, query, collection, where, deleteField } from 'firebase/firestore';
import { db } from '../firebase';

/*
 * Function: addWorkspaceMember
 * Description: Adds a new member to a workspace and all its projects.
 * Parameters:
 *   workspace: object - The workspace object to add the member to.
 *   email: string - Email of the user to add.
 *   userId: string - ID of the user performing the action.
 * Returns: Promise<void>
 * Usage:
 *   await addWorkspaceMember(workspace, 'user@example.com', currentUserId);
 * AI Hint: Implement invitation system with pending status for users who haven't yet joined the platform.
 */
export const addWorkspaceMember = async (workspace, email, userId) => {
    const usersSnapshot = await getDocs(
        query(collection(db, 'users'), where('email', '==', email.toLowerCase()))
    );

    if (usersSnapshot.empty) {
        throw new Error('User not found');
    }

    const newMember = usersSnapshot.docs[0];
    const newMemberId = newMember.id;

    if (workspace.members[newMemberId]) {
        throw new Error('User is already a member');
    }

    const workspaceRef = doc(db, 'workspaces', workspace.id);
    const projectsSnapshot = await getDocs(
        query(collection(db, 'projects'), where('workspaceId', '==', workspace.id))
    );

    const memberData = {
        role: 'member',
        addedAt: new Date().toISOString(),
        addedBy: userId
    };

    await updateDoc(workspaceRef, {
        [`members.${newMemberId}`]: memberData
    });

    const projectUpdates = projectsSnapshot.docs.map(project =>
        updateDoc(doc(db, 'projects', project.id), {
            [`members.${newMemberId}`]: memberData
        })
    );

    await Promise.all(projectUpdates);
};

/*
 * Function: removeWorkspaceMember
 * Description: Removes a member from a workspace and all its projects.
 * Parameters:
 *   workspace: object - The workspace object to remove the member from.
 *   memberId: string - ID of the member to remove.
 * Returns: Promise<void>
 * Usage:
 *   await removeWorkspaceMember(workspace, 'member123');
 * AI Hint: Implement audit logging to track member removals for security purposes.
 */
export const removeWorkspaceMember = async (workspace, memberId) => {
    const workspaceRef = doc(db, 'workspaces', workspace.id);
    const projectsSnapshot = await getDocs(
        query(collection(db, 'projects'), where('workspaceId', '==', workspace.id))
    );

    await updateDoc(workspaceRef, {
        [`members.${memberId}`]: deleteField()
    });

    const projectUpdates = projectsSnapshot.docs.map(project =>
        updateDoc(doc(db, 'projects', project.id), {
            [`members.${memberId}`]: deleteField()
        })
    );

    await Promise.all(projectUpdates);
};

/*
 * Function: updateMemberRole
 * Description: Updates a member's role in a workspace and all its projects.
 * Parameters:
 *   workspace: object - The workspace object.
 *   memberId: string - ID of the member to update.
 *   newRole: string - The new role to assign ('admin' or 'member').
 * Returns: Promise<void>
 * Usage:
 *   await updateMemberRole(workspace, 'member123', 'admin');
 * AI Hint: Implement real-time updates for member role changes using WebSockets for a more interactive experience.
 */
export const updateMemberRole = async (workspace, memberId, newRole) => {
    const workspaceRef = doc(db, 'workspaces', workspace.id);
    const projectsSnapshot = await getDocs(
        query(collection(db, 'projects'), where('workspaceId', '==', workspace.id))
    );

    await updateDoc(workspaceRef, {
        [`members.${memberId}.role`]: newRole
    });

    const projectUpdates = projectsSnapshot.docs.map(project =>
        updateDoc(doc(db, 'projects', project.id), {
            [`members.${memberId}.role`]: newRole
        })
    );

    await Promise.all(projectUpdates);
};